import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CollectionGuide, TopicLinks, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourGrid } from '@todo/web-ui/components/tour-grid';
import { collectionsOfKind } from '@todo/web-ui/lib/collections';
import { localePath } from '@todo/web-ui/lib/format';
import { matchesSearch, normalizeSearch, searchWords } from '@todo/web-ui/lib/search';
import { TOURS_INDEX_QUERY, TOURS_INDEX_SEED_KEY } from '@/lib/prefetch';
import { useTours } from '@todo/core/hooks/use-tours';
import { SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { breadcrumbJsonLd, faqPageJsonLd, seoTitle, toursCrumb } from '@todo/core/seo';
import type { Tour } from '@todo/core/tours';

import css from './tours.module.css';

/** Whether every word of the query is in one of the tour's titles (any locale). */
function matches(tour: Tour, words: string[]): boolean {
  const titles = [tour.title, ...SUPPORTED_LOCALES.map((l) => tour.translations?.[l]?.title ?? '')].join(' ');
  return matchesSearch(normalizeSearch(titles), words);
}

// The seven categories, linked under the intro: the hub's way into the shorter lists.
const CATEGORIES = collectionsOfKind('category');

// Every tour in Miami, best ranked first — and the hero search's results
// page: `?q=` filters the list by title. dubai's screen: the query is read
// after mount, so the first client render matches the prerendered
// (unfiltered) HTML; SEO <title>, keyword <h1> and, under the cards, a guide
// + FAQ (FAQPage JSON-LD) from `collections.tours` in the resources. No
// "Read also" guides (no Sanity project yet). Indexable once it lists a tour,
// as dubai's.
export default function ToursIndex() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const { tours, loading } = useTours(TOURS_INDEX_QUERY, TOURS_INDEX_SEED_KEY);
  const params = useLocalSearchParams<{ q?: string }>();
  const [query, setQuery] = useState('');
  const [mounted, setMounted] = useState(false);
  const copy = useCollectionCopy('tours');

  useEffect(() => setMounted(true), []);

  // `?q=` from the router, not window.location: on a client-side navigation
  // from the home search the address isn't updated yet when this mounts. Only
  // after mount (the prerendered HTML is the unfiltered list), and not while
  // the field already holds the same query — typing writes `q` back trimmed,
  // which must not eat a trailing space.
  useEffect(() => {
    if (!mounted) return;
    const q = typeof params.q === 'string' ? params.q : '';
    setQuery((current) => (current.trim() === q.trim() ? current : q));
  }, [mounted, params.q]);

  const words = useMemo(() => searchWords(query), [query]);
  // Memoized: TourGrid starts over from its first page whenever this list changes.
  const shown = useMemo(() => (words.length ? tours.filter((tour) => matches(tour, words)) : tours), [tours, words]);

  const updateQuery = (value: string) => {
    setQuery(value);
    // Keep the URL shareable without a navigation per keystroke.
    const q = value.trim();
    router.setParams({ q: q || undefined } as never);
  };

  return (
    <Page>
      <PageHead
        title={seoTitle(t('collections.tours.seoTitle'))}
        description={t('collections.tours.meta')}
        path="tours"
        indexable={tours.length > 0}>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(locale, [{ name: toursCrumb(locale).name }]))}</script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(TOURS_INDEX_SEED_KEY)}
      </PageHead>

      <Section id="tours-title" level={1} title={t('collections.tours.heading')}>
        <Intro>{t('collections.tours.intro')}</Intro>
        <TopicLinks collections={CATEGORIES} />

        {tours.length ? (
          <>
            <form role="search" className={css.search} action={localePath(locale, 'tours')} onSubmit={(e) => e.preventDefault()}>
              <label className={css.label} htmlFor="tours-q">
                {t('tours.searchLabel')}
              </label>
              <input
                id="tours-q"
                type="search"
                name="q"
                value={query}
                onChange={(e) => updateQuery(e.target.value)}
                placeholder={t('tours.searchPlaceholder')}
                className={css.input}
              />
            </form>
            {words.length ? (
              <p className={css.status} aria-live="polite">
                {shown.length ? t('tours.resultsFor', { query: query.trim() }) : t('tours.noResults', { query: query.trim() })}{' '}
                <button type="button" className={css.clear} onClick={() => updateQuery('')}>
                  {t('tours.clearSearch')}
                </button>
              </p>
            ) : null}
            <TourGrid tours={shown} />
          </>
        ) : loading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <EmptyState exclude="tours" />
        )}
      </Section>

      <CollectionGuide slug="tours" copy={copy} />
    </Page>
  );
}
